import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'flowbite-react';
import OTPInput from 'react-otp-input';
import toast from 'react-hot-toast';
import { mobileOTPSignup, mobileOTPSignUpVerify } from '../../api/auth';
import {
  handleVerifyResponse,
  showGuestAccountHint,
} from '../../utils/landingAuth';
import AccountHintBanner from '../AccountHintBanner/AccountHintBanner';
import FieldError, { ButtonSpinner } from '../FieldError/FieldError';
import { getApiErrorMessage, validateLandingMobile } from '../../utils/landingForm';
import '../OtpModal/OtpModal.css';
import '../Drawer/Drawer.css';

/**
 * Guest OTP — never sends visitor:true (creates/continues GUEST).
 * changeMobile: editing an already-verified number — do not clear session on open/cancel.
 */
const GuestOtpModal = ({ isOpen, onClose, onGuestReady, changeMobile = false }) => {
  const [step, setStep] = useState('mobile'); // mobile | otp
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [accountHint, setAccountHint] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const mobileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    // Fresh number entry when changing; never wipe localStorage from the modal.
    setStep('mobile');
    setMobile('');
    setOtp('');
    setError('');
    setAccountHint(null);
    setSending(false);
    setVerifying(false);
    setResending(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && step === 'mobile' && mobileInputRef.current) {
      mobileInputRef.current.focus();
    }
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen || step !== 'otp') return;
    const focusFirst = () => {
      const el = document.querySelector('.custom-otp-field');
      if (el && typeof el.focus === 'function') el.focus();
    };
    const t1 = window.setTimeout(focusFirst, 50);
    const t2 = window.setTimeout(focusFirst, 200);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isOpen, step]);

  const handleMobileChange = (e) => {
    setMobile(e.target.value.replace(/\D/g, '').slice(0, 10));
    setAccountHint(null);
    if (error) setError('');
  };

  const sendOtp = async () => {
    const validationError = validateLandingMobile(mobile);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSending(true);
    setError('');
    try {
      // No visitor:true → GUEST account for new mobiles
      const mobileStatus = await mobileOTPSignup({ mobile });
      if (mobileStatus?.status >= 400) {
        setError(getApiErrorMessage(mobileStatus, 'Could not send OTP'));
        return;
      }
      const hint = showGuestAccountHint(mobileStatus);
      setAccountHint(hint);
      if (hint) toast(hint, { icon: 'ℹ️' });
      else toast.success('OTP sent successfully');
      setStep('otp');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not send OTP. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const resendOtp = async () => {
    if (!mobile || resending || verifying) return;
    setResending(true);
    setError('');
    try {
      const mobileStatus = await mobileOTPSignup({ mobile });
      if (mobileStatus?.status >= 400) {
        setError(getApiErrorMessage(mobileStatus, 'Could not resend OTP'));
        return;
      }
      const hint = showGuestAccountHint(mobileStatus);
      if (hint) setAccountHint(hint);
      toast.success('OTP resent successfully');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not resend OTP.'));
    } finally {
      setResending(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const updateApi = await mobileOTPSignUpVerify({
        mobile,
        otp,
        device_id: 'landing-guest-web',
        ip: 'landing-page',
        // intentionally no visitor:true
      });
      const outcome = handleVerifyResponse(updateApi, mobile, {
        onCloseModals: ({ clearInputs, action } = {}) => {
          if (clearInputs) {
            setMobile('');
            setOtp('');
            setAccountHint(null);
          }
          // stay_on_guest: onGuestReady already resumed pending work — closing via
          // parent onClose would clearPendingGuestAction and cancel the continuation.
          if (action === 'stay_on_guest') {
            return;
          }
          onClose?.();
        },
        onGuestReady: () => {
          onGuestReady?.();
        },
        onShowRegistrationForm: () => {
          toast(
            'This number is already set up for a business account. Try another number, or go Home to continue.',
          );
          onClose?.();
        },
      });
      if (!outcome.ok) {
        setError(outcome.message || 'OTP verification failed');
        return;
      }
      if (outcome.action === 'stay_on_guest') {
        // Parent onGuestReady closes the modal after resuming interest / pay.
        setStep('mobile');
        setOtp('');
        setAccountHint(null);
      }
      toast.success(outcome.message || 'Signed in');
    } catch (err) {
      setError(getApiErrorMessage(err, 'OTP verification failed.'));
    } finally {
      setVerifying(false);
    }
  };

  const busy = sending || verifying || resending;

  return (
    <Modal show={isOpen} size="sm" popup onClose={onClose}>
      <Modal.Body>
        {step === 'mobile' ? (
          <div className="flex flex-col -mb-2">
            <h1 className="text-2xl mb-2 mt-2 font-bold text-[#13224d]">
              {changeMobile ? 'Change mobile number' : 'Continue with mobile'}
            </h1>
            <p className="mb-3 text-sm text-[#333333]">
              {changeMobile
                ? 'Enter the new number and verify with OTP. Your current number stays active if you cancel.'
                : "Enter your number. We'll send a one-time code so you can show interest or join a chit."}
            </p>
            <input
              ref={mobileInputRef}
              onChange={handleMobileChange}
              className={`custom-number-input border-[1px] border-dotted w-full rounded-lg placeholder:text-xs ${
                error ? 'border-red-500 ring-1 ring-red-200' : 'border-slate-400'
              }`}
              type="text"
              inputMode="numeric"
              placeholder="10-digit mobile"
              value={mobile}
              maxLength={10}
              disabled={busy}
            />
            <FieldError message={error} />
            <div className="flex justify-end mt-4 gap-2">
              <Button color="light" size="sm" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
              <Button
                color="dark"
                size="sm"
                className="bg-primaryColor"
                disabled={busy}
                onClick={sendOtp}
              >
                {sending ? (
                  <span className="inline-flex items-center gap-2">
                    <ButtonSpinner /> Sending…
                  </span>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center p-2">
            <h1 className="text-2xl mb-2 font-bold text-[#13224d]">Verify OTP</h1>
            <p className="mb-2 text-sm text-gray-500">
              Sent to <span className="font-bold text-[#13224d]">+91 {mobile}</span>
            </p>
            <AccountHintBanner message={accountHint} className="mb-3 text-left" />
            <div className="flex flex-col items-center">
              <OTPInput
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  if (error) setError('');
                }}
                inputType="tel"
                numInputs={6}
                shouldAutoFocus
                inputStyle={`custom-otp-field${error ? ' otp-field-error' : ''}`}
                renderInput={(props) => (
                  <input {...props} disabled={busy} />
                )}
              />
              <FieldError message={error} className="w-full max-w-[280px] text-center mt-2" />
            </div>
            <div className="flex justify-center mt-4">
              <Button
                color="dark"
                size="sm"
                className="bg-primaryColor"
                disabled={busy || otp.length !== 6}
                onClick={verifyOtp}
              >
                {verifying ? (
                  <span className="inline-flex items-center gap-2">
                    <ButtonSpinner /> Verifying…
                  </span>
                ) : (
                  'Verify & continue'
                )}
              </Button>
            </div>
            <p className="text-sm mt-3 text-gray-500">
              Didn&apos;t get it?{' '}
              <button
                type="button"
                className={`underline ${busy ? 'text-gray-400' : 'text-teal-600'}`}
                disabled={busy}
                onClick={resendOtp}
              >
                {resending ? 'Sending…' : 'Resend'}
              </button>
            </p>
            <button
              type="button"
              className="text-xs text-gray-400 underline mt-2"
              disabled={busy}
              onClick={() => {
                setStep('mobile');
                setOtp('');
                setError('');
              }}
            >
              Change number
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GuestOtpModal;
